import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  DatePicker,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  MenuToggle,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Spinner,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  CheckCircleIcon,
  EllipsisVIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  FileContractIcon,
  SearchIcon,
} from '@patternfly/react-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice } from '@fortawesome/free-solid-svg-icons/faFileInvoice';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useApiFetch } from '../../api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useInvoices } from '../../hooks/useInvoices';
import type { Invoice, InvoicePaymentStatus } from '../../types/models/Invoice';
import { formatDate } from '../../utils/dateFormat';
import PayInvoiceModal from './PayInvoiceModal';

const AMOUNT_FORMATTER = new Intl.NumberFormat('fr-BE', {
  style: 'currency',
  currency: 'EUR',
});

function formatAmount(value: string): string {
  const n = parseFloat(value);
  return Number.isNaN(n) ? value : AMOUNT_FORMATTER.format(n);
}

const isCreditNote = (invoice: Invoice): boolean => invoice.type === 'note de credit';

// A GPU server's monthly usage invoice (title `Serveur GPU srv-… — <mois année>`) is issued ALREADY
// SETTLED: its hours were debited from the prepaid balance as they ran, and the backend refuses to
// pay it again. Facturier can still show it unpaid for a day or more (the mark-paid step retries
// with the daily invoicer), so it never offers Pay and never reads "Outstanding"
// (docs/api-types/gpu-servers.md).
const GPU_SERVER_INVOICE_PREFIX = 'Serveur GPU srv-';
const isGpuServerInvoice = (invoice: Invoice): boolean => invoice.intitule.startsWith(GPU_SERVER_INVOICE_PREFIX);

// Credit notes render as a red negative amount. Facturier does not document the sign of
// credit-note amounts (passthrough strings), so normalise display-side via -Math.abs():
// the result is negative whichever sign the API returned.
function renderAmount(invoice: Invoice): React.ReactNode {
  const raw = invoice.amounts.tvac;
  if (!isCreditNote(invoice)) {
    return formatAmount(raw);
  }
  const n = parseFloat(raw);
  return (
    <span style={{ color: 'var(--pf-t--global--text--color--status--danger--default)' }}>
      {Number.isNaN(n) ? raw : AMOUNT_FORMATTER.format(n === 0 ? 0 : -Math.abs(n))}
    </span>
  );
}

// Type filter values: 'all' = no filter; the other two compare against Facturier `FactureType`
// literals. `Invoice.type` deliberately stays a free `string` (Facturier has more FactureType
// values than these two) — we only special-case the two we expose in the UI.
type InvoiceTypeFilter = 'all' | 'officielle' | 'note de credit';
// Status filter values map to the `paye` field. No 'credite' option by design — credited invoices
// appear only under "All statuses".
type InvoiceStatusFilter = 'all' | 'oui' | 'non';

const TYPE_FILTER_OPTIONS: ReadonlyArray<{ value: InvoiceTypeFilter; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'officielle', label: 'Invoice' },
  { value: 'note de credit', label: 'Credit note' },
];

const STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: InvoiceStatusFilter; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'oui', label: 'Paid' },
  { value: 'non', label: 'Outstanding' },
];

const isInvoiceTypeFilter = (v: unknown): v is InvoiceTypeFilter =>
  v === 'all' || v === 'officielle' || v === 'note de credit';

const isInvoiceStatusFilter = (v: unknown): v is InvoiceStatusFilter => v === 'all' || v === 'oui' || v === 'non';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Strict YYYY-MM-DD parser shared by the DatePickers (via dateParse) and the commit gate below.
// JS Date APIs roll invalid calendar dates over instead of rejecting them (Date.parse('2026-02-31')
// is 2026-03-03), so a round-trip check is required; anything not matching the exact shape
// (including stray whitespace) is invalid too. Keeping the pickers' parser and the commit gate
// identical means the input's "Invalid date" error and the applied filter can never contradict
// each other.
function parseStrictIsoDate(value: string): Date {
  if (!ISO_DATE_RE.test(value)) {
    return new Date(NaN);
  }
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  const roundTrips = parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
  return roundTrips ? parsed : new Date(NaN);
}

// A DatePicker fires onChange on every keystroke, so its raw value only becomes a commit CANDIDATE
// once it is a complete, real date — a partial or invalid value means "no date filter", never a
// request with a half-typed or rolled-over bound (which the upstream would reject, swapping the
// toolbar for an error state with no way to fix the date).
function toApiDate(value: string): string | undefined {
  return Number.isNaN(parseStrictIsoDate(value).getTime()) ? undefined : value;
}

function renderPaymentStatus(status: InvoicePaymentStatus, settledFromBalance: boolean = false): React.ReactNode {
  if (settledFromBalance && status === 'non') {
    return (
      <Label color="green" icon={<CheckCircleIcon />}>
        Settled from your prepaid balance
      </Label>
    );
  }
  switch (status) {
    case 'oui':
      return (
        <Label color="green" icon={<CheckCircleIcon />}>
          Paid
        </Label>
      );
    case 'credite':
      return <Label color="blue">Credited</Label>;
    case 'non':
    default:
      return (
        <Label color="orange" icon={<ExclamationTriangleIcon />}>
          Outstanding
        </Label>
      );
  }
}

function Invoices() {
  const auth = useAuth();

  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  // The invoice the Pay modal is open for, and whether it is mid-submission/redirect (reported up
  // by the modal) so every row's Pay item is aria-disabled meanwhile — two rows must never race.
  const [payingInvoice, setPayingInvoice] = useState<Invoice | null>(null);
  const [paymentBusy, setPaymentBusy] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<InvoiceTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatusFilter>('all');
  // Raw DatePicker input values; see toApiDate for what actually reaches the API.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isTypeSelectOpen, setIsTypeSelectOpen] = useState(false);
  const [isStatusSelectOpen, setIsStatusSelectOpen] = useState(false);

  // Search hits the server, so commit the typed value only after a 400ms pause.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const fromCandidate = toApiDate(fromDate);
  const toCandidate = toApiDate(toDate);

  // Committed date bounds. Like the search text, candidates commit only after a 400ms pause:
  // editing an already-committed date de-commits it on the first keystroke, and without the
  // debounce that would fire an intermediate request (and a page reset) for a range the user
  // never chose. An inverted range never commits its upper bound — the "to" picker's validator
  // (below) tells the user why it isn't applied.
  const [fromFilter, setFromFilter] = useState<string | undefined>(undefined);
  const [toFilter, setToFilter] = useState<string | undefined>(undefined);
  useEffect(() => {
    const handle = setTimeout(() => {
      setFromFilter(fromCandidate);
      setToFilter(fromCandidate && toCandidate && toCandidate < fromCandidate ? undefined : toCandidate);
    }, 400);
    return () => clearTimeout(handle);
  }, [fromCandidate, toCandidate]);

  // Reset to page 1 whenever a filter commits, otherwise the user can land on a now out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeFilter, statusFilter, fromFilter, toFilter]);

  // Search (`q` reaches invoice notes, project numbers and line descriptions — data the browser
  // never receives), Type/Status filters, the date range and pagination are all server-side;
  // 'all' means "omit the filter" and must never be sent.
  const { invoices, pagination, loading, error, refetch } = useInvoices({
    page,
    pageSize: perPage,
    q: debouncedSearch,
    type: typeFilter === 'all' ? undefined : typeFilter,
    paye: statusFilter === 'all' ? undefined : statusFilter,
    from: fromFilter,
    to: toFilter,
  });

  const downloadInvoice = useCallback(
    async (invoice: Invoice) => {
      setDownloadingId(invoice.id);
      try {
        const response = await apiFetch(`/clients/invoices/${invoice.id}/pdf`);
        const contentType = response.headers.get('Content-Type') ?? '';

        // Success and failure are BOTH HTTP 200 — the Content-Type is the only discriminator.
        if (contentType.includes('application/pdf')) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `facture-${invoice.numero}.pdf`; // the backend imposes no filename
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return;
        }

        // Otherwise it's a JSend error (application/json) at HTTP 200 — surface its message.
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        const message = body?.message ?? 'Could not download the invoice. Please try again.';
        addNotification({
          title: 'Could not download invoice',
          srTitle: 'Invoice download error',
          description: message,
          variant: 'danger',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not download invoice',
          srTitle: 'Invoice download error',
          description: err instanceof Error ? err.message : 'An unexpected network error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setDownloadingId(null);
      }
    },
    [apiFetch, addNotification, setDrawerOpen],
  );

  // "Pay invoice" opens the payment-source modal (prepaid balance / saved card / hosted checkout);
  // the modal owns the POST and its redirect — see PayInvoiceModal.
  const openPayModal = useCallback((invoice: Invoice) => {
    setPayingInvoice(invoice);
  }, []);

  const closePayModal = useCallback(() => {
    setPayingInvoice(null);
  }, []);

  const handlePaid = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  // Row actions rendered inside the per-row kebab menu. Pay is offered only for outstanding official
  // invoices (never for a GPU server invoice, already settled from the prepaid balance); Download is
  // always available (including for credit notes). While an action is in flight the item is
  // aria-disabled (not hard-disabled) so it stays focusable and its tooltip explains why.
  const buildRowActions = (invoice: Invoice): IAction[] => {
    const actions: IAction[] = [];

    if (invoice.paye === 'non' && invoice.type === 'officielle' && !isGpuServerInvoice(invoice)) {
      actions.push({
        title: 'Pay invoice',
        onClick: () => openPayModal(invoice),
        // Disable every Pay item while a payment is being submitted — on a checkout we redirect to
        // Mollie, so a second start must not race the navigation.
        ...(paymentBusy ? { isAriaDisabled: true, tooltipProps: { content: 'Starting payment…' } } : {}),
      });
    }

    actions.push({
      title: 'Download PDF',
      onClick: () => void downloadInvoice(invoice),
      ...(downloadingId === invoice.id ? { isAriaDisabled: true, tooltipProps: { content: 'Downloading…' } } : {}),
    });

    return actions;
  };

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const onTypeSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    if (isInvoiceTypeFilter(value)) {
      setTypeFilter(value);
    }
    setIsTypeSelectOpen(false);
  };

  const onStatusSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    if (isInvoiceStatusFilter(value)) {
      setStatusFilter(value);
    }
    setIsStatusSelectOpen(false);
  };

  const clearFilters = () => {
    setSearchValue('');
    setTypeFilter('all');
    setStatusFilter('all');
    setFromDate('');
    setToDate('');
  };

  const typeToggleLabel = TYPE_FILTER_OPTIONS.find((o) => o.value === typeFilter)?.label ?? 'All types';
  const statusToggleLabel = STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label ?? 'All statuses';

  const renderContent = () => {
    // Initial load only (no response seen yet) — a refetch or filter change keeps the table
    // mounted instead of flashing a full-page spinner over already-rendered rows.
    if (loading && pagination === null && !error) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load invoices"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    // Genuinely no invoices — zero matches with no filter active. A zero-total page WITH a filter
    // active falls through to the toolbar + "no match" state below, so the filter can be cleared.
    // (Also covers a client with no Facturier accounting_key, which the backend returns as an
    // empty list — no distinguishing flag.) The !loading guard matters: right after CLEARING a
    // zero-result filter, `pagination` is still the stale `{total: 0}` while the unfiltered
    // refetch runs — without the guard this branch would falsely claim "no invoices yet" (and
    // unmount the toolbar) for the whole round trip.
    const hasActiveFilters =
      debouncedSearch !== '' ||
      typeFilter !== 'all' ||
      statusFilter !== 'all' ||
      fromFilter !== undefined ||
      toFilter !== undefined;
    if (!loading && (pagination?.total ?? 0) === 0 && !hasActiveFilters) {
      return (
        <EmptyState titleText="No invoices yet" icon={FileContractIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Your organization has no invoices on file yet. New invoices will appear here once issued.
          </EmptyStateBody>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by number, title, notes, line items..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value.slice(0, 200))}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem>
              <Select
                isOpen={isTypeSelectOpen}
                selected={typeFilter}
                onSelect={onTypeSelect}
                onOpenChange={(isOpen) => setIsTypeSelectOpen(isOpen)}
                toggle={(toggleRef) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsTypeSelectOpen(!isTypeSelectOpen)}
                    isExpanded={isTypeSelectOpen}
                    aria-label="Filter by invoice type"
                  >
                    {typeToggleLabel}
                  </MenuToggle>
                )}
              >
                <SelectList>
                  {TYPE_FILTER_OPTIONS.map((option) => (
                    <SelectOption key={option.value} value={option.value}>
                      {option.label}
                    </SelectOption>
                  ))}
                </SelectList>
              </Select>
            </ToolbarItem>
            <ToolbarItem>
              <Select
                isOpen={isStatusSelectOpen}
                selected={statusFilter}
                onSelect={onStatusSelect}
                onOpenChange={(isOpen) => setIsStatusSelectOpen(isOpen)}
                toggle={(toggleRef) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsStatusSelectOpen(!isStatusSelectOpen)}
                    isExpanded={isStatusSelectOpen}
                    aria-label="Filter by payment status"
                  >
                    {statusToggleLabel}
                  </MenuToggle>
                )}
              >
                <SelectList>
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <SelectOption key={option.value} value={option.value}>
                      {option.label}
                    </SelectOption>
                  ))}
                </SelectList>
              </Select>
            </ToolbarItem>
            <ToolbarItem>
              <DatePicker
                value={fromDate}
                onChange={(_event, value) => setFromDate(value)}
                dateParse={parseStrictIsoDate}
                aria-label="Filter from date"
                buttonAriaLabel="Toggle from date calendar"
                placeholder="From (YYYY-MM-DD)"
              />
            </ToolbarItem>
            <ToolbarItem>
              <DatePicker
                value={toDate}
                onChange={(_event, value) => setToDate(value)}
                dateParse={parseStrictIsoDate}
                aria-label="Filter to date"
                buttonAriaLabel="Toggle to date calendar"
                placeholder="To (YYYY-MM-DD)"
                rangeStart={fromCandidate ? parseStrictIsoDate(fromCandidate) : undefined}
                validators={[
                  (date) =>
                    fromCandidate && date < parseStrictIsoDate(fromCandidate)
                      ? 'Must be on or after the from date'
                      : '',
                ]}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={pagination?.total ?? 0}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {invoices.length === 0 ? (
          <EmptyState titleText="No invoices match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>
              No invoices match your current search and filters. Try a different term, or clear the filters to see all
              invoices.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={clearFilters}>
                  Clear filters
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <>
            <Table aria-label="Invoices list" variant="compact" borders={false}>
              <Thead>
                <Tr>
                  <Th>Invoice #</Th>
                  <Th>Title</Th>
                  <Th>Date</Th>
                  <Th>Amount (TVAC)</Th>
                  <Th>Status</Th>
                  <Th screenReaderText="Actions" />
                </Tr>
              </Thead>
              <Tbody>
                {invoices.map((invoice) => (
                  <Tr key={invoice.id}>
                    <Td dataLabel="Invoice #">{invoice.numero}</Td>
                    <Td dataLabel="Title">
                      {invoice.intitule}
                      {isCreditNote(invoice) && (
                        <Label isCompact color="purple" style={{ marginLeft: '8px' }}>
                          Credit note
                        </Label>
                      )}
                    </Td>
                    <Td dataLabel="Date">{formatDate(invoice.date)}</Td>
                    <Td dataLabel="Amount (TVAC)">{renderAmount(invoice)}</Td>
                    <Td dataLabel="Status">{renderPaymentStatus(invoice.paye, isGpuServerInvoice(invoice))}</Td>
                    <Td dataLabel="Actions" isActionCell>
                      <ActionsColumn
                        items={buildRowActions(invoice)}
                        actionsToggle={({ onToggle, isOpen, isDisabled, toggleRef }) => (
                          <MenuToggle
                            ref={toggleRef}
                            aria-label={`Actions for invoice ${invoice.numero}`}
                            variant="plain"
                            onClick={onToggle}
                            isExpanded={isOpen}
                            isDisabled={isDisabled}
                            // Show a spinner on the row's kebab while its PDF is downloading. The menu
                            // closes on click, so the in-menu "Downloading…" disabled state is hidden;
                            // surfacing progress on the toggle restores the in-row feedback the old
                            // inline download button gave via isLoading.
                            icon={
                              downloadingId === invoice.id ? (
                                <Spinner size="md" aria-label="Downloading invoice" />
                              ) : (
                                <EllipsisVIcon />
                              )
                            }
                          />
                        )}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <Pagination
              itemCount={pagination?.total ?? 0}
              perPage={perPage}
              page={page}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
              variant="bottom"
            />
          </>
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/organization" isActive>
            Organization
          </BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <FontAwesomeIcon icon={faFileInvoice} />
          </Icon>
          Invoices
        </Content>
        <Content component="p">Browse and download invoices issued to your organization.</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
      <PayInvoiceModal
        invoice={payingInvoice}
        isOpen={payingInvoice !== null}
        onClose={closePayModal}
        onPaid={handlePaid}
        onBusyChange={setPaymentBusy}
      />
    </>
  );
}

export default Invoices;
